/**
 * ProcurementRequestCard — Worksheet "Alkatrész igény" panel (S-126-04 slice 1).
 *
 * Phase 1 scope:
 *  - List existing ProcurementRequest rows for this worksheet
 *  - "Új alkatrész igény" dialog: product picker (search), quantity, notes
 *  - POST /procurement-requests with source=WORKSHEET + worksheetId
 *
 * NOT in slice 1:
 *  - Worksheet close-gate warning (slice 2 — Q-126-01 ratify pending, Phase 1 default = warning)
 *  - Edit / delete / status-change of existing requests (slice 2)
 */

import type { ProcurementRequest, ProcurementRequestStatus } from '@/api/procurement';
import type { Product } from '@/api/products';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useCreateProcurementRequest,
  useProcurementRequests,
  useUpdateProcurementRequest,
} from '@/hooks/use-procurement-requests';
import { useProducts } from '@/hooks/use-products';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';

interface ProcurementRequestCardProps {
  worksheetId: string;
  disabled?: boolean;
}

const STATUS_BADGE: Record<ProcurementRequest['status'], string> = {
  DRAFT: 'bg-muted text-foreground',
  SUBMITTED: 'bg-blue-100 text-blue-800',
  ORDERED: 'bg-yellow-100 text-yellow-800',
  RECEIVED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800',
};

/**
 * Phase 1 state-machine (Q-126-03 ratify Sprint 23 W0 D-2).
 * Mirrors backend `ALLOWED_TRANSITIONS` in procurement.service.ts.
 */
const FORWARD_TRANSITION: Partial<Record<ProcurementRequestStatus, ProcurementRequestStatus>> = {
  DRAFT: 'SUBMITTED',
  SUBMITTED: 'ORDERED',
  ORDERED: 'RECEIVED',
};

function forwardActionLabelKey(status: ProcurementRequestStatus): string | null {
  switch (status) {
    case 'DRAFT':
      return 'procurement.actions.submit';
    case 'SUBMITTED':
      return 'procurement.actions.markOrdered';
    case 'ORDERED':
      return 'procurement.actions.markReceived';
    default:
      return null;
  }
}

const CANCELLABLE_STATUSES: ProcurementRequestStatus[] = ['DRAFT', 'SUBMITTED'];

export function ProcurementRequestCard({ worksheetId, disabled }: ProcurementRequestCardProps) {
  const { t } = useTranslation();
  const { requests, meta, isTruncated, isLoading, error, refetch } = useProcurementRequests({
    worksheetId,
  });
  const updateMutation = useUpdateProcurementRequest();
  const [open, setOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleTransition = async (req: ProcurementRequest, to: ProcurementRequestStatus) => {
    setActionError(null);
    try {
      await updateMutation.mutateAsync({ id: req.id, input: { status: to } });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t('procurement.errors.transitionRejected', { from: req.status, to });
      setActionError(message);
    }
  };

  const handleCancel = async (req: ProcurementRequest) => {
    if (!window.confirm(t('procurement.actions.confirmCancel'))) return;
    await handleTransition(req, 'CANCELLED');
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>{t('procurement.worksheetPanelTitle')}</CardTitle>
          {!disabled && (
            <Button
              type="button"
              size="sm"
              onClick={() => setOpen(true)}
              data-testid="procurement-add-button"
            >
              {t('procurement.addRequest')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {actionError && (
          <p
            className="mb-2 text-sm text-red-600 dark:text-red-400"
            role="alert"
            data-testid="procurement-action-error"
          >
            {actionError}
          </p>
        )}
        {isLoading ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : error ? (
          <p className="text-sm text-red-600 dark:text-red-400">
            {t('service.errorPrefix', { message: error })}
          </p>
        ) : requests.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('procurement.emptyForWorksheet')}</p>
        ) : (
          <ul className="space-y-2" data-testid="procurement-request-list">
            {requests.map(req => {
              const forwardTo = FORWARD_TRANSITION[req.status];
              const forwardLabelKey = forwardActionLabelKey(req.status);
              const canCancel = CANCELLABLE_STATUSES.includes(req.status);
              const showActions = !disabled && (forwardTo || canCancel);
              return (
                <li
                  key={req.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded border border-gray-200 bg-white p-3 text-sm dark:border-gray-700 dark:bg-gray-800"
                  data-testid={`procurement-request-row-${req.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {t('procurement.productIdShort')}: {req.productId.slice(0, 8)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t('procurement.quantity')}: {req.quantity}
                      {req.notes ? ` · ${req.notes}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[req.status]}`}
                      data-testid={`procurement-status-${req.id}`}
                    >
                      {t(`procurement.status.${req.status}`)}
                    </span>
                    {showActions && (
                      <div className="flex items-center gap-1">
                        {forwardTo && forwardLabelKey && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={updateMutation.isPending}
                            onClick={() => void handleTransition(req, forwardTo)}
                            data-testid={`procurement-forward-${req.id}`}
                          >
                            {t(forwardLabelKey)}
                          </Button>
                        )}
                        {canCancel && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={updateMutation.isPending}
                            onClick={() => void handleCancel(req)}
                            data-testid={`procurement-cancel-${req.id}`}
                            className="text-red-600 dark:text-red-400"
                          >
                            {t('procurement.actions.cancel')}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* #1034 follow-up: ha a BE lapoz, a lista CSONKÍTOTT — enélkül a felhasználó
            egy rövidebb listát látna, és semmi nem jelezné, hogy van még. A néma
            csonkítás rosszabb, mint a hosszú lista: az látszik, ez nem. */}
        {isTruncated && meta && (
          <p
            className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-2 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
            role="status"
            data-testid="procurement-truncated-notice"
          >
            {t('procurement.listTruncated', {
              shown: String(requests.length),
              total: String(meta.total),
            })}
          </p>
        )}
      </CardContent>

      <AddProcurementRequestDialog
        open={open}
        onOpenChange={setOpen}
        worksheetId={worksheetId}
        onCreated={() => {
          void refetch();
          setOpen(false);
        }}
      />
    </Card>
  );
}

interface AddDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  worksheetId: string;
  onCreated: () => void;
}

function AddProcurementRequestDialog({
  open,
  onOpenChange,
  worksheetId,
  onCreated,
}: AddDialogProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [notes, setNotes] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { products, isLoading: searching } = useProducts(
    search.length >= 2 ? { search, pageSize: 8 } : { pageSize: 0 }
  );
  const createMutation = useCreateProcurementRequest();

  const reset = () => {
    setSearch('');
    setSelected(null);
    setQuantity('1');
    setNotes('');
    setSubmitError(null);
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!selected) {
      setSubmitError(t('procurement.errors.productRequired'));
      return;
    }
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty <= 0) {
      setSubmitError(t('procurement.errors.quantityInvalid'));
      return;
    }
    try {
      await createMutation.mutateAsync({
        productId: selected.id,
        quantity: qty,
        source: 'WORKSHEET',
        worksheetId,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      reset();
      onCreated();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('common.unknownError'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('procurement.addRequestTitle')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('procurement.productSearchLabel')}
            </label>
            <Input
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setSelected(null);
              }}
              placeholder={t('procurement.productSearchPlaceholder')}
              data-testid="procurement-product-search"
            />
            {search.length >= 2 && !selected && (
              <ul className="mt-2 max-h-48 overflow-y-auto rounded border border-gray-200 dark:border-gray-700">
                {searching ? (
                  <li className="px-3 py-2 text-sm text-muted-foreground">{t('common.loading')}</li>
                ) : products.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-muted-foreground">
                    {t('procurement.noProductsFound')}
                  </li>
                ) : (
                  products.map(p => (
                    <li key={p.id}>
                      <button
                        type="button"
                        className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                        onClick={() => {
                          setSelected(p);
                          setSearch(p.name);
                        }}
                      >
                        <span className="font-medium">{p.name}</span>
                        <span className="ml-2 text-xs text-muted-foreground">{p.sku}</span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
            {selected && (
              <p className="mt-1 text-xs text-muted-foreground">
                {t('procurement.selectedProduct')}: {selected.name} ({selected.sku})
              </p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('procurement.quantity')}
            </label>
            <Input
              type="number"
              min={0}
              step={0.001}
              value={quantity}
              onChange={e => setQuantity(e.target.value)}
              data-testid="procurement-quantity"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('procurement.notes')}
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              maxLength={2000}
              className="w-full rounded-md border border-gray-300 bg-white p-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              placeholder={t('procurement.notesPlaceholder')}
            />
          </div>

          {submitError && (
            <p className="text-sm text-red-600 dark:text-red-400" role="alert">
              {submitError}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleClose(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending || !selected}
              data-testid="procurement-submit"
            >
              {createMutation.isPending ? t('common.saving') : t('procurement.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
